import { useEffect } from "react";
import axios from "axios";
import useRefreshToken from './../hooks/useRefreshToken';
import useAuth from "./useAuth";
import Config from "../config";
import Utils from "../utils";
import { markActivity } from "../utils/activityTracker";

const baseURL = `${Config.URL_SERVER}/api`;

const instance = axios.create({
    baseURL,
    headers: { Accept: 'application/json'},
    withCredentials: true,
});

const extractFiles = (data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        return Array.from(data.values()).filter((value) => value instanceof File);
    }
    if (data && typeof data === 'object') {
        return Object.values(data).filter((value) => value instanceof File);
    }
    return [];
};

const useAxiosPrivate = () => {
    const refresh = useRefreshToken();
    const { auth, setUploads } = useAuth();

    useEffect(() => {
        const reqIntercept = instance.interceptors.request.use(
            config => {
                markActivity();

                if (!config.headers['Authorization']) {
                    config.headers['Authorization'] = `Bearer ${auth?.token}`;
                }

                const contentType = config.headers?.get ? config.headers.get('Content-Type') : config.headers?.['Content-Type'];
                const isMultipart = contentType?.includes('multipart/form-data') || (typeof FormData !== 'undefined' && config.data instanceof FormData);

                if (isMultipart && !config.__uploadId) {
                    const files = extractFiles(config.data);

                    if (files.length) {
                        const controller = new AbortController();
                        config.signal = controller.signal;

                        const uploadId = `${Date.now()}-${Math.random()}`;
                        config.__uploadId = uploadId;

                        setUploads(prev => [...prev, {
                            id: uploadId,
                            name: files[0].name,
                            extraCount: files.length - 1,
                            size: files.reduce((sum, file) => sum + file.size, 0),
                            extension: Utils.getFileExtension(files[0].name),
                            progress: 0,
                            controller,
                        }]);

                        let lastProgress = 0;
                        config.onUploadProgress = (progressEvent) => {
                            if (!progressEvent.total) return;
                            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            if (progress === lastProgress) return;
                            lastProgress = progress;
                            setUploads(prev => prev.map((upload) => upload.id === uploadId ? {...upload, progress} : upload));
                        };
                    }
                }

                return config;
            }, (error) => Promise.reject(error)
        )

        const resIntercept = instance.interceptors.response.use(
            (response) => {
                if (response.config?.__uploadId) {
                    setUploads(prev => prev.filter((upload) => upload.id !== response.config.__uploadId));
                }
                return response;
            }, (error) => {
                const prevReq = error?.config;
                if (prevReq?.__uploadId) {
                    setUploads(prev => prev.filter((upload) => upload.id !== prevReq.__uploadId));
                }

                if (error?.response?.status === 401 && !prevReq?.sent) {
                    prevReq.sent = true;
                    const newAccessToken = refresh();
                    prevReq.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return instance(prevReq);
                }
                return Promise.reject(error);
            }
        );

        return () => {
            instance.interceptors.request.eject(reqIntercept);
            instance.interceptors.response.eject(resIntercept);
        }
    }, [auth]);

    return instance;
}

export default useAxiosPrivate;
